// One camera for every altitude.
//
// The Atlas used to carry a rig per tier: a fixed heliocentric pose, five
// hand-tuned orbit framings, a clip pair each and a switch at the top of the
// ladder to get back out. All of it is replaced by one focus point, one
// altitude, and a yaw/pitch orbit around it, and the altitude alone decides
// which band the player is in (docs/roadmap/02-continuous-descent.md).
//
// Bands are a PURE function of altitude and carry a label, the UI density and
// an LOD hint - nothing structural. The tier index the HUD prints is a second
// pure function of the same altitude, kept so the ladder's own labels and
// `?atlasTier=N` survive the change.

import * as THREE from 'three';

import type { WorldVec } from '../world/frame';

export type Band = 'SYSTEM' | 'ORBIT' | 'REGIONAL' | 'LOCAL' | 'CLAIM' | 'PLOT' | 'SURFACE';

/**
 * Lower edge of each band, metres above the datum.
 *
 * SYSTEM and ORBIT are geometric in the body's radius - the angular radius
 * falling below a quarter of the half-frame, and the whole disc first fitting
 * it - so they scale with the body. The four below are the plot lattice and the
 * terrain pyramid's floors, which are the same on every solid body. On a body
 * small enough for the geometric edges to fall under the fixed ones (Bennu's
 * 9.2 radii is 2.2 km) each edge is pushed under the one above it, so the
 * ladder stays monotone rather than crossing over.
 */
export interface BandEdges {
  readonly SYSTEM: number;
  readonly ORBIT: number;
  readonly REGIONAL: number;
  readonly LOCAL: number;
  readonly CLAIM: number;
  readonly PLOT: number;
}

export function bandEdges(radiusM: number): BandEdges {
  const system = 9.2 * radiusM;
  const orbit = Math.min(1.613 * radiusM, system * 0.5);
  const regional = Math.min(220e3, orbit * 0.5);
  const local = Math.min(25e3, regional * 0.5);
  const claim = Math.min(3.5e3, local * 0.5);
  const plot = Math.min(500, claim * 0.5);
  return { SYSTEM: system, ORBIT: orbit, REGIONAL: regional, LOCAL: local, CLAIM: claim, PLOT: plot };
}

export function bandAt(altitudeM: number, radiusM: number): Band {
  const e = bandEdges(radiusM);
  if (altitudeM >= e.SYSTEM) return 'SYSTEM';
  if (altitudeM >= e.ORBIT) return 'ORBIT';
  if (altitudeM >= e.REGIONAL) return 'REGIONAL';
  if (altitudeM >= e.LOCAL) return 'LOCAL';
  if (altitudeM >= e.CLAIM) return 'CLAIM';
  if (altitudeM >= e.PLOT) return 'PLOT';
  return 'SURFACE';
}

/**
 * Where the LOCAL band splits its two tier labels.
 *
 * Seven bands against eight ladder rungs: the legacy ladder has two hex tiers
 * inside one band (HEX CELL in context, HEX SURVEY close in) and its last two
 * rungs, PLOT and Walkaround, are a selection and a mode rather than an
 * altitude. So LOCAL carries two labels and SURFACE shares PLOT's.
 */
const LOCAL_SPLIT_M = 60e3;

/** The ladder rung a given altitude reads as, 0 (heliocentric) to 6 (plot). */
export function tierAt(altitudeM: number, radiusM: number): number {
  const band = bandAt(altitudeM, radiusM);
  switch (band) {
    case 'SYSTEM': return 0;
    case 'ORBIT': return 1;
    case 'REGIONAL': return 2;
    case 'LOCAL': return altitudeM >= LOCAL_SPLIT_M ? 3 : 4;
    case 'CLAIM': return 5;
    default: return 6;
  }
}

/**
 * A definite altitude per rung, so `?atlasTier=N` still lands somewhere exact.
 *
 * Each is inside the band `tierAt` maps back to that rung, and each is the
 * framing that rung was hand-tuned to: the hemisphere at 3.5 radii, a
 * quadrangle at 1.4, the claim's 36 plots filling the frame at 16.9 km
 * (02-continuous-descent.md, "Altitude bands").
 */
export function altitudeForTier(tier: number, radiusM: number): number {
  switch (tier) {
    case 1: return 3.5 * radiusM;
    case 2: return 1.4 * radiusM;
    case 3: return 120e3;
    case 4: return 40e3;
    case 5: return 16.9e3;
    default: return 1.5e3;
  }
}

/** Metres per rendered pixel looking straight down, the band table's ruler. */
export function metresPerPixel(altitudeM: number, fovDeg: number, heightPx: number): number {
  return (2 * altitudeM * Math.tan((fovDeg * 0.5 * Math.PI) / 180)) / Math.max(1, heightPx);
}

// Scratch for `isOnScreen` below: every caller only reads the boolean back,
// never these vectors, so one pair reused across calls replaces the two
// clones the function used to allocate per call.
const _onScreenView = new THREE.Vector3();
const _onScreenNdc = new THREE.Vector3();

/**
 * Whether a camera-relative position, in scene metres, projects inside the
 * visible frame.
 *
 * `Vector3.project`'s NDC z is not a safe front/behind test at Atlas scale:
 * with `camera.near` at the orrery's own 0.0005 and positions out past 1e13, the hyperbolic
 * z-mapping puts a point that far in front of the eye and one that far behind
 * it at the same float64 value, so `p.z` alone cannot tell them apart. Raw
 * view-space z against the near plane can, so that is checked first.
 */
export function isOnScreen(camera: THREE.PerspectiveCamera, worldPos: THREE.Vector3): boolean {
  const view = _onScreenView.copy(worldPos).applyMatrix4(camera.matrixWorldInverse);
  if (view.z >= -camera.near) return false;
  const ndc = _onScreenNdc.copy(worldPos).project(camera);
  return Math.abs(ndc.x) <= 1 && Math.abs(ndc.y) <= 1;
}

/**
 * How far the camera may pitch below the focus point's horizontal.
 *
 * Orbiting an anchor at radius `a` and distance `h`, the eye is outside a shell
 * of radius `clear` only while
 * `sin(pitch) >= (clear^2 - a^2 - h^2) / (2 a h)` - the law of cosines, written
 * as a pitch. With the anchor ON the shell that is the horizon: level with the
 * ground close in, and no floor at all two radii out, because looking up at a
 * planet from underneath is a real view. The anchor's OWN radius is what it is
 * derived from, not the body's: a surface anchor sits on the drawn terrain,
 * which at Jezero is 2.5 km under the datum, and a datum-derived floor put the
 * eye that far inside the body on a far-side approach.
 */
export function pitchFloor(altitudeM: number, anchorRadiusM: number, clearRadiusM: number): number {
  const a = Math.max(1, anchorRadiusM);
  const h = Math.max(1e-6, altitudeM);
  const s = (clearRadiusM * clearRadiusM - a * a - h * h) / (2 * a * h);
  if (s <= -1) return -PITCH_LIMIT;
  if (s >= 1) return PITCH_LIMIT;
  return Math.asin(s);
}

/**
 * How far above the ground the eye is kept, as a fraction of its own altitude.
 *
 * Scale-free on purpose: a metre constant that is nothing at 5,000 km is the
 * whole frame at 2 m, and the clearance has to mean the same thing at both
 * ends of a hundred-notch descent. A twentieth of the altitude is a pitch floor
 * of 2.87 degrees over the anchor's own horizontal at every altitude the
 * walkaround holds, which is under `atlas.ts`'s own `PLOT_PITCH_FLOOR` of
 * 0.14 rad: inside the walkaround that floor is what binds, and this one is
 * what holds the approach above it.
 */
export const EYE_CLEAR_FRACTION = 0.05;

/**
 * Metres the eye is held over the ground when the altitude floor binds.
 *
 * The walkaround's own rig floors its eye at the same height
 * (`SURFACE_EYE_FLOOR_M`, src/atlas.ts), so the two agree across the handover.
 */
export const EYE_GROUND_FLOOR_M = 5;

/** Never straight down the pole: yaw stops meaning anything there. */
export const PITCH_LIMIT = 1.4;

/**
 * How much of the half-frame the horizon may sit up at the closed-in oblique.
 *
 * The standing pose aims `pitch` below the eye's own horizontal and the horizon
 * sits on that horizontal - a tenth of a degree under it at Jezero - so the
 * oblique the ceiling closes to is a fact about the FRAME and not a constant.
 * A fifth of the upper half-frame is left over the horizon for ground that
 * stands above the shell the eye is on.
 */
export const HORIZON_FRAME_FRACTION = 0.8;

/**
 * Where the ceiling has finished closing, metres of altitude.
 *
 * The last stretch is what fills the frame with near ground, not the floor
 * alone: a ramp that reached its oblique at the clamp was still 34.6 degrees
 * down at 10 m and read a flat band over 8.6 per cent of the frame there. It is
 * also the stretch where a pitch still easing costs a frame - every notch is a
 * new pose and the pyramid streams for it - so the ceiling is asked to be done
 * BEFORE it rather than during it: at 8 m the same descent still read one pose
 * at 7.9 per cent, at 8.44 m, where the ease had a fifth of a degree left to
 * run and the same pose read clean once it had settled.
 */
export const EYE_OBLIQUE_ALT_M = 20;

/** The pre-fix oblique and its altitude: `surface-clamp --band --inject focused-aim`. */
export const LEGACY_EYE_OBLIQUE = 0.44;
export const LEGACY_EYE_OBLIQUE_ALT_M = 2;

/** The steepest aim that still has the horizon inside a frame this wide. */
export function eyeOblique(halfFovRad: number): number {
  const half = Math.max(0.02, Math.min(1.5, halfFovRad));
  return Math.min(LEGACY_EYE_OBLIQUE, Math.atan(HORIZON_FRAME_FRACTION * Math.tan(half)));
}

/**
 * How far the camera may pitch ABOVE it.
 *
 * Descending should end up looking along the ground rather than straight down
 * at it, so the ceiling closes from nearly overhead at the top of LOCAL to a
 * low oblique close in. Log-interpolated, because altitude is.
 *
 * The oblique it closes to is the frame's own (`eyeOblique`), because this
 * ceiling is the only thing that unwinds a rung's pitch on the way down and a
 * rung is steeper the lower it is: `applyTierFraming` in `src/atlas.ts` frames
 * tier 1 at 0.35 rad and tier 2 at 0.75, and nothing reframes a wheel-only
 * descent afterwards. The old 0.44 rad is 25.2 degrees against a 22.5 degree
 * half-frame, so a descent that arrived from tier 2 ended with the horizon off
 * the top edge at ndcY 1.14 and the near ground flat over 17.9 per cent of the
 * frame, while the same descent from tier 1 never reached the ceiling at all
 * and never showed it.
 */
export function pitchCeiling(
  altitudeM: number,
  obliqueRad = eyeOblique(Math.PI / 8),
  obliqueAltM = EYE_OBLIQUE_ALT_M,
): number {
  const hi = 25e3;
  const lo = Math.max(1, Math.min(hi / 2, obliqueAltM));
  if (altitudeM >= hi) return PITCH_LIMIT;
  const t = Math.log(Math.max(lo, altitudeM) / lo) / Math.log(hi / lo);
  return obliqueRad + (PITCH_LIMIT - obliqueRad) * Math.max(0, Math.min(1, t));
}

/** One wheel notch. 1.18^100 spans the top of ORBIT to an eye height of 1.7 m. */
export const ZOOM_PER_NOTCH = 1.18;

/**
 * How far the aim has converged from the body's own centre onto its focus point.
 *
 * A body with surveyed ground has a place the descent ends at, and aiming there
 * from the first frame is what put Mars's disc a third of a frame below centre
 * at ORBIT and pivoted the wheel on Jezero from the far side of the planet.
 * While the whole planet is the subject the aim is its centre; the site becomes
 * the subject over the band under ORBIT and is the whole aim below it. Log
 * altitude, smoothstepped, so neither band edge steps.
 */
export function anchorConvergence(altitudeM: number, radiusM: number): number {
  const e = bandEdges(radiusM);
  if (!(e.ORBIT > e.REGIONAL)) return 1;
  if (altitudeM >= e.ORBIT) return 0;
  if (altitudeM <= e.REGIONAL) return 1;
  const u = Math.log(e.ORBIT / altitudeM) / Math.log(e.ORBIT / e.REGIONAL);
  return u * u * (3 - 2 * u);
}

export interface FocusBodies {
  radiusM(bodyId: string): number | null;
  /** Body centre, scene axes, metres. */
  centre(bodyId: string): WorldVec | null;
  /** Body north pole as a unit vector in scene axes. */
  pole(bodyId: string): WorldVec | null;
  /**
   * A point on the body's sphere of `radius_m` at a body-fixed lat/lon, scene
   * axes, metres. Spherical, not the datum ellipsoid - that arrives with the
   * Phase 2 surfaces.
   */
  surface(bodyId: string, latDeg: number, lonDeg: number): WorldVec | null;
  /** Whether descent below ORBIT is offered for this body. */
  descendable(bodyId: string): boolean;
  /**
   * The altitude the body's own data floor stops the descent at, or null where
   * nothing stops it above the ground.
   *
   * Two classes answer it, both off the registry row (`src/atlas.ts`'s
   * `descentFloor`). An imagery-only body has no elevation under it at any
   * level, so below the altitude where a screen pixel is finer than one imagery
   * cell every further metre is magnification. A gas or ice giant has no
   * surface at all: the floor is the bottom edge of its cloud-top corridor,
   * which is the deck plus the lowest band's own edge. A body with a height
   * pyramid answers null and keeps the 1.7 m eye floor. Answering non-null is
   * also what makes a `descend: false` body reachable below ORBIT.
   */
  floorAltitudeM?(bodyId: string): number | null;
  /**
   * Radius of the DRAWN ground under a point, body centre to surface, metres.
   *
   * What the eye's own clamp reads instead of the datum: half of Mars is
   * kilometres below the datum and Olympus is 21 km above it, so no one sphere
   * is the ground. Null where nothing is drawn under the point, or where
   * another rig owns the camera and this one must not move it.
   */
  groundRadiusM?(bodyId: string, point: WorldVec): number | null;
  /**
   * Radius the eye may not descend below under a point, metres, or null.
   *
   * `groundRadiusM` is what the PITCH floor reads and this is what the ALTITUDE
   * floor reads, and they differ in what a null means. A null here is "no floor
   * at all", which is what the walkaround asks for while its own rig owns the
   * camera; a body with nothing drawn under the point answers its own datum
   * instead, so a globe-map-only body still has a bottom.
   */
  eyeFloorRadiusM?(bodyId: string, point: WorldVec): number | null;
  /**
   * A tighter pitch ceiling than the altitude's own, radians, or null.
   *
   * For the one class that has no ground to look down at: inside a gas giant's
   * cloud-top corridor, straight down is a featureless deck and the view canon
   * asks for is the limb in side view (`src/atlas/corridor.ts`). Held here
   * rather than in `pitchCeiling` because it is a fact about the body and not
   * about the altitude.
   */
  pitchCeilingFor?(bodyId: string, altitudeM: number): number | null;
  /**
   * Whether this body can only be focused at its CENTRE, because it has no
   * surface to focus on.
   *
   * A gas giant. It matters because altitude is the distance to the focus
   * POINT: on a centre anchor the eye sits at exactly `radius + altitude`,
   * which is the altitude a cloud-top corridor's band edges are declared in,
   * while on a surface anchor at a low pitch the eye is only `altitude * sin
   * (pitch)` above the datum - 20 km of a declared 162.
   */
  centreAnchorOnly?(bodyId: string): boolean;
  /**
   * How far to raise the LOOK direction off the anchor, radians, or null.
   *
   * A centre anchor looks at the centre, which is straight down: from inside a
   * cloud-top corridor that is a featureless deck filling the frame, and the
   * three strata are all behind the camera. Raised to just above the body's
   * own horizon instead, which is canon 5.4's side view - the limb low in the
   * frame with the corridor stacked over it. Measured from the eye's own local
   * horizontal, so 0 is level and a negative value looks down.
   */
  lookLiftRad?(bodyId: string, altitudeM: number): number | null;
  /**
   * How far over the anchor the eye is held at all, metres, or null.
   *
   * Altitude is the distance to the focus POINT, so on a surface anchor at a
   * low pitch the eye is only `altitude * sin(pitch)` above it: at the SURFACE
   * clamp's 1.7 m and a low oblique that is centimetres, an ant's view of the
   * ground rather than a person's. Answered by the Atlas in the band whose
   * label promises a walkaround and null everywhere else. `steppedIn` is the
   * handle's `steppedIntoSurface`.
   */
  standingEyeFloorM?(bodyId: string, altitudeM: number, steppedIn: boolean): number | null;
  /** The bodies a framing has to hold, as scene-axis metres. */
  framingTargets(scope: FramingScope): readonly WorldVec[];
}

/**
 * Which bodies a heliocentric framing has to hold.
 *
 * `opening` is the opening view: a fixed reach in heliocentric semi-major axis
 * (`src/atlas.ts`'s `OPENING_FIT_MAX_A_AU`), which is what keeps it a property
 * of the solar system rather than of whichever rows happen to carry ground.
 * `all` is the set Phase 1's pick criterion is about - every body projecting in
 * front of the camera and staying hittable - and is where `descent-dojo`'s pick
 * sweep runs.
 */
export type FramingScope = 'opening' | 'all';

export interface FocusCameraConfig {
  camera: THREE.PerspectiveCamera;
  bodies: FocusBodies;
  /**
   * The focused body's radius when the registry has no row for it, metres.
   *
   * A function and not a number because the body is picked from the URL before
   * the registry fetch lands, so there is nothing to read at construction.
   */
  fallbackRadiusM: () => number;
  /** Withdraw the eye's ground floor. DEV only; the control that reads it. */
  groundFloorOff?: () => boolean;
  /** Never owe a stand its ease, so a notch inside it holds the eye (`?standeaseoff=1`). DEV only. */
  standEaseOff?: () => boolean;
}

export interface FocusState {
  bodyId: string;
  /** Null puts the focus on the body centre, which is what ORBIT and up want. */
  latDeg: number | null;
  lonDeg: number | null;
  altitudeM: number;
  yaw: number;
  pitch: number;
}

export interface FocusCameraHandle {
  readonly state: FocusState;
  band(): Band;
  tier(): number;
  radiusM(): number;
  /**
   * Metres the eye's ground floor added to the altitude the last time the eye
   * was placed; zero when it did not bind.
   *
   * Above zero says the focus point is under the ground the eye can see, which
   * is what `updateFocusGround` in src/atlas/focusGround.ts answers by closing
   * its own lag.
   */
  readonly groundFloorHeldM: number;
  /**
   * Whether a wheel notch set the altitude last. A goTo or setAltitude asks for
   * metres over the ground, so the eye follows that ground as it eases in.
   */
  readonly wheelZoomed: boolean;
  /**
   * A goTo or setAltitude came down into SURFACE and the ground ease after it
   * has not closed: the eye follows that ease, neither floored nor pitch-cleared,
   * even if a wheel notch lands inside it. A notch that leaves SURFACE drops it.
   */
  readonly standEaseOwed: boolean;
  /**
   * What the focus ground's ease did this frame: `moving`, `closed` (which pays
   * the stand once it has moved), or `stood`, the walkaround having taken the eye.
   */
  noteStandEase(ease: 'moving' | 'closed' | 'stood'): void;
  /** The altitude came down into SURFACE by `zoom` or `setAltitude` from a band above it, not by `goTo`. */
  readonly steppedIntoSurface: boolean;
  /** Multiply the altitude by ZOOM_PER_NOTCH^notches. Works in every band. */
  zoom(notches: number): void;
  /** How many `zoom` calls have zoomed in: the player's own steps, since nothing else calls it. */
  readonly zoomInSteps: number;
  setAltitude(metres: number): void;
  /** Clamp and floor the altitude where it stands, after its bounds or the ground moved; not an ask, so `wheelZoomed` and `standEaseOwed` keep. */
  reclampAltitude(): void;
  /**
   * Re-measure the altitude so the eye keeps the radius it has from the body
   * centre.
   *
   * For the one caller that moves the FOCUS POINT itself: the altitude is
   * measured to that point, so easing it onto the terrain otherwise walks the
   * eye up with it (`updateFocusGround`, src/atlas/focusGround.ts).
   */
  holdEyeRadius(radiusFromCentreM: number): void;
  orbitBy(dYaw: number, dPitch: number): void;
  /** Set yaw and pitch outright, clamping pitch the way every other write does. */
  setOrientation(yaw: number, pitch: number, remember?: number): void;
  /**
   * Stop the pitch following altitude. Until a drag (or this) pins it, the
   * pitch a caller set is the one wanted at every altitude the ceiling allows,
   * so zooming back out unwinds what zooming in clamped.
   */
  pinPitch(): void;
  /** The pitch zooming back out restores, or null while the pitch follows nothing. */
  pitchMemory(): number | null;
  /** Move the focus point, leaving yaw, pitch and altitude exactly as they are. */
  setAnchor(latDeg: number | null, lonDeg: number | null): void;
  /**
   * The unit direction, scene axes, from the body centre to the point THIS pose
   * is descending onto, or null with no body.
   *
   * Not the point under the camera: the eye sits `atan2(h cos pitch, a + h sin
   * pitch)` of arc from its own anchor - 44 degrees at the ORBIT edge at the
   * ladder's 0.35 rad - so an anchor set to the sub-camera point moves the eye
   * by that arc, and the pitch ceiling makes the two coincide only at 90
   * degrees. This is the anchor whose own basis leaves the eye where it already
   * is, solved by iteration because the basis is built from the anchor.
   */
  poseAnchorDir(): WorldVec | null;
  /**
   * Yaw 0's north and east in scene axes at the placed pose; north drifts round the ground as the body turns.
   */
  yawFrame(north: THREE.Vector3, east: THREE.Vector3): void;
  goTo(bodyId: string, latDeg: number | null, lonDeg: number | null, altitudeM: number): void;
  /** Frame the scope's targets with a margin; the heliocentric default view. */
  fitAll(aspect: number, scope?: FramingScope, keepPitchMemory?: boolean): void;
  /** The eye, scene axes, metres: what the camera-relative frame subtracts. */
  eyeWorld(): WorldVec;
  /**
   * Turn the eye onto a direction from the body's centre, leaving the aim, the
   * anchor and the altitude where they are.
   *
   * Exact where the pose is built on the body's own axis, which is every
   * framing at and above the ORBIT band edge; the ground clearance and the
   * pitch ceiling still bind, so a caller that needs to know it arrived
   * measures the placed eye rather than trusting the ask.
   */
  faceDirection(dirWorld: WorldVec): void;
  /** Point the three.js camera, which sits at the scene origin. */
  apply(): void;
  /**
   * Restore the pre-fix floor derivation: `farside-check`'s negative control.
   *
   * The body's radius in place of the anchor's, with no clearance - which is
   * what put the eye 2,566 m inside Mars on a far-side descent.
   */
  setLegacyPitchFloor(on: boolean): void;
  /**
   * Restore the pre-fix aim: the site pivot from the first frame, at every
   * altitude. `body-sweep --focus --inject site-pivot`'s negative control.
   */
  setLegacySitePivot(on: boolean): void;
  /**
   * Restore the pre-fix basis: the site's own local frame at every altitude,
   * with the aim left where it is. `body-sweep --focus --inject site-axis`.
   */
  setLegacySiteAxis(on: boolean): void;
  /**
   * Restore the pre-fix oblique: 0.44 rad reached at the clamp rather than the
   * frame's own reached at `EYE_OBLIQUE_ALT_M`.
   *
   * `surface-clamp --band --inject focused-aim`.
   */
  setLegacyEyeOblique(on: boolean): void;
  /**
   * Restore the pre-fix pivot: the eye's radius swinging with the pitch about
   * the focus point at every convergence, including the bands where the aim is
   * the body's own centre. `descent-dojo --pivot --inject orbit-pivot`.
   */
  setLegacyOrbitPivot(on: boolean): void;
  /**
   * Restore the pre-fix yaw reference: the axis re-picked from the basis the
   * convergence is turning rather than from the body's pole, which is what
   * threw the eye round a near-ecliptic-pole body on one wheel notch.
   * `descent-dojo --crossing --inject basis-yaw-axis`.
   */
  setLegacyYawAxis(on: boolean): void;
  /**
   * Restore the unguarded standing solve: no divergence stop in `standEye`, and
   * the mast floor lifting a standing eye by its own margin on every call.
   * `descent-dojo --stand --inject stand-unguarded`.
   */
  setLegacyStandSolve(on: boolean): void;
  /**
   * Restore the reference crossed at the LIVE up rather than carried from the
   * pole: the rule that tested the pole alone, which the site's own blend
   * still walked across on a zoom and the body's spin across twice a day.
   * `descent-dojo --crossing --inject pole-yaw-axis`.
   */
  setLegacyPoleYawAxis(on: boolean): void;
  /** What the aim has converged onto, and where the eye sits around it. */
  aim(): FocusAim;
  dispose(): void;
}

/** The aim reading: `anchorConvergence` as the camera applied it. */
export interface FocusAim {
  /** 0 on the body centre, 1 on the focus point, null with no focus point. */
  convergence: number | null;
  /** The focus point's own distance from the body centre, metres, or null. */
  anchorRadiusM: number | null;
  /** Eye radial against the focus point's, degrees of arc at the centre. */
  eyeArcDeg: number | null;
  /** Eye radial against the body's own pole, degrees of arc at the centre. */
  poleArcDeg: number | null;
  /** Whether the legacy site pivot is armed. */
  sitePivot: boolean;
  /** Whether the legacy site basis is armed. */
  siteAxis: boolean;
  /** Whether the legacy pitch-swung pivot is armed. */
  orbitPivot: boolean;
  /** Whether the legacy basis-derived yaw reference is armed. */
  yawAxisFromBasis: boolean;
  /** Whether the legacy pole-picked, live-up-crossed yaw reference is armed. */
  yawAxisFromPole: boolean;
  /** The scene axis yaw 0 is measured from, as the pose stands: 'x' or 'y'. */
  yawAxis: string;
}

const _up = new THREE.Vector3();
const _east = new THREE.Vector3();
const _north = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _look = new THREE.Vector3();
const _radial = new THREE.Vector3();
const _h = new THREE.Vector3();
const _pole = new THREE.Vector3();
const _face = new THREE.Vector3();
const _siteHat = new THREE.Vector3();
const _aim = new THREE.Vector3();
const _probeEye = new THREE.Vector3();
const _probeUp = new THREE.Vector3();
const _probeDir = new THREE.Vector3();
const _probeQ = new THREE.Quaternion();
const YSCENE = new THREE.Vector3(0, 1, 0);
const XSCENE = new THREE.Vector3(1, 0, 0);
const _axisProbe = new THREE.Vector3();

/**
 * The scene axis yaw 0 is measured from, as the pose currently stands.
 *
 * Held rather than re-derived inside `poseDir` because the choice has to be
 * made off something that does not move while the player zooms - see
 * `setYawAxis`.
 */
const _yawAxis = new THREE.Vector3().copy(YSCENE);

/** The pole that reference was picked against, and the turn onto the live up. */
const _yawPole = new THREE.Vector3().copy(YSCENE);
const _yawTurn = new THREE.Quaternion();

/**
 * Pick the yaw reference off the BODY'S OWN POLE, not off the basis in hand.
 *
 * `poseDir` needs a direction that is not parallel to the basis it is building
 * on, and the pair it picks between are 90 degrees apart, so which one is in
 * hand decides where yaw 0 points. `turnAxis` walks that basis from the pole
 * onto the focus point's radial as the aim converges, so a choice re-made from
 * the basis every frame FLIPS partway down the band - and the eye, whose
 * azimuth is built on it, jumps round the body by whatever the two references
 * are apart. On a body whose pole already sits inside the 2.56 degree cone
 * about the scene Y axis the walk crosses it a notch or two under the ORBIT
 * edge: Io's pole is 2.07 degrees off it, Europa's 2.08 and Luna's 0.01, and
 * all three threw the eye 15 to 114 degrees round the body on one wheel notch
 * with nothing dragged. A pole does not move while a player zooms, so a choice
 * made off it holds for the whole descent. Mars, at 23.4 degrees, picks the
 * same axis either way and nothing about its ladder moves.
 *
 * The pole is kept beside it, because that is the only vector this test
 * clears the reference of: `poseEast` builds the frame AT the pole and carries
 * it onto the live up, so nothing else ever has to be crossed with it.
 */
function setYawAxis(poleVec: WorldVec | null): void {
  _yawAxis.copy(YSCENE);
  _yawPole.copy(YSCENE);
  if (!poleVec) return;
  _axisProbe.set(poleVec.x, poleVec.y, poleVec.z);
  if (_axisProbe.lengthSq() === 0) return;
  _yawPole.copy(_axisProbe.normalize());
  if (Math.abs(_yawPole.dot(YSCENE)) > 0.999) _yawAxis.copy(XSCENE);
}

function vec(v: WorldVec, out: THREE.Vector3): THREE.Vector3 {
  return out.set(v.x, v.y, v.z);
}

export function createFocusCamera(config: FocusCameraConfig): FocusCameraHandle {
  const { camera, bodies } = config;
  const state: FocusState = {
    bodyId: 'sol',
    latDeg: null,
    lonDeg: null,
    altitudeM: 1.9e13,
    yaw: 0.6,
    pitch: 1.33,
  };
  const eye: WorldVec = { x: 0, y: 0, z: 0 };
  const anchor: WorldVec = { x: 0, y: 0, z: 0 };
  /** Metres the eye's ground floor added to the altitude the last time it ran.
   *  Above zero says the focus point is under the ground the eye can see. */
  let groundFloorHeldM = 0;
  /** Whether a wheel notch, and not a goTo or setAltitude, set the altitude last. */
  let wheelZoomed = false;
  /** A goTo or setAltitude into SURFACE, until the ground ease after it has run and closed. */
  let standEase: 'none' | 'owed' | 'easing' = 'none';
  const askStand = (): void => {
    standEase = bandAt(state.altitudeM, radiusM()) === 'SURFACE' && config.standEaseOff?.() !== true ? 'owed' : 'none';
  };
  let zoomInSteps = 0;
  let steppedIn = false;
  function noteStep(fromM: number): void {
    const r = radiusM();
    steppedIn = bandAt(state.altitudeM, r) === 'SURFACE' && (steppedIn || bandAt(fromM, r) !== 'SURFACE');
  }
  /** The pitch the last setOrientation asked for, or null once a drag has taken the pitch over. */
  let wantPitch: number | null = null;
  let legacyFloor = false;
  let legacySitePivot = false;
  let legacySiteAxis = false;
  let legacyEyeOblique = false;
  let legacyOrbitPivot = false;
  let legacyYawAxis = false;
  let legacyStandSolve = false;
  let legacyPoleYawAxis = false;

  /** How much of the aim is the focus point rather than the body centre. */
  function aimMix(): number {
    return legacySitePivot ? 1 : anchorConvergence(state.altitudeM, radiusM());
  }

  /**
   * The radius the converged pose holds at this pitch, squared.
   *
   * Every blended pose keeps it, which is what makes one pitch floor cover the
   * whole convergence: the floor is a statement about this radius and nothing
   * else. Law of cosines about the centre, with the eye a distance `h` from a
   * focus point at radius `a`.
   *
   * The pitch term carries the same `mix` the aim does. With the aim on the
   * focus point the eye is `h` from it and its own radius swings with the
   * pitch, which is what the close descent frames on; with the aim on the body
   * centre, which is the whole ORBIT band and everything above it, the altitude
   * is a height over the body and must not move when the player tilts. At mix 0
   * this is exactly `(a + h)^2`, the radius a centre-anchored body already
   * holds, so one reported altitude is one distance on a body with a site and
   * on a body without one alike. Left unmixed it read 3,667 km at the bottom of
   * Mars's ORBIT band at the pitch floor against 8,825 km at the ceiling, one
   * reported altitude of 5,468 km.
   */
  function convergedRadiusSq(anchorRadiusM: number, mix: number): number {
    const h = state.altitudeM;
    const swing = legacyOrbitPivot ? Math.sin(state.pitch)
      : mix * Math.sin(state.pitch) + (1 - mix);
    return anchorRadiusM * anchorRadiusM + 2 * anchorRadiusM * h * swing + h * h;
  }

  /**
   * The eye's distance from an aim pulled `mix` of the way in from the focus
   * point towards the body centre.
   *
   * Solved so the eye keeps the radius above: a straight blend between the two
   * poses cuts a chord through the body, and the only answer a pitch floor has
   * to that is to climb over the site.
   *
   * `cosOffset` is the eye direction's own component along the line from the
   * centre to the focus point, which is where the anchor sits. It used to be
   * read as `sin(pitch)`, and that held while the basis was the focus point's
   * own frame at every convergence; the basis turns towards the pole as the aim
   * does now, so the direction has to be asked rather than assumed - assuming
   * it put the eye 600 km inside Mars on a far-side approach through the ramp.
   */
  function aimDistanceM(anchorRadiusM: number, mix: number, cosOffset: number): number {
    if (mix >= 1) return state.altitudeM;
    const p = anchorRadiusM * mix;
    const converged = convergedRadiusSq(anchorRadiusM, mix);
    return Math.max(1, -p * cosOffset
      + Math.sqrt(Math.max(0, converged - p * p * (1 - cosOffset * cosOffset))));
  }

  function radiusM(): number {
    return bodies.radiusM(state.bodyId) ?? config.fallbackRadiusM();
  }

  function minAltitude(): number {
    // The row's own floor first, whether or not there is ground: an
    // imagery-only body stops at its imagery floor and a gas giant at the
    // bottom edge of its cloud-top corridor, and neither is a surface.
    const floor = bodies.floorAltitudeM?.(state.bodyId) ?? null;
    if (floor !== null) return floor;
    // A body with no ground to stand on and no floor of its own stops where its
    // whole disc is in frame; `availability.descend` is what says which.
    if (!bodies.descendable(state.bodyId)) return bandEdges(radiusM()).ORBIT;
    return 1.7;
  }

  function clampAltitude(m: number): number {
    return Math.min(1e15, Math.max(minAltitude(), m));
  }

  /** Whether the focus is the body centre, whichever way it got there. */
  function centreAnchored(): boolean {
    return state.latDeg === null || state.lonDeg === null
      || (bodies.centreAnchorOnly?.(state.bodyId) ?? false);
  }

  /** The anchor's own distance from the body centre, or null on a centre anchor. */
  function anchorRadius(): number | null {
    if (centreAnchored() || state.latDeg === null || state.lonDeg === null) return null;
    const s = bodies.surface(state.bodyId, state.latDeg, state.lonDeg);
    if (!s) return null;
    const c = bodies.centre(state.bodyId) ?? { x: 0, y: 0, z: 0 };
    return Math.hypot(s.x - c.x, s.y - c.y, s.z - c.z);
  }

  /** The ceiling the altitude gives, tightened by whatever the body asks for. */
  function ceilingAt(altitudeM: number): number {
    const own = bodies.pitchCeilingFor?.(state.bodyId, altitudeM) ?? null;
    const mine = legacyEyeOblique
      ? pitchCeiling(altitudeM, LEGACY_EYE_OBLIQUE, LEGACY_EYE_OBLIQUE_ALT_M)
      : pitchCeiling(altitudeM, eyeOblique((camera.fov * Math.PI) / 360));
    return own === null ? mine : Math.min(mine, own);
  }

  function clampPitch(p: number): number {
    // A centre anchor puts the eye at `r + altitude` whatever the pitch, so the
    // floor there is the body's own radius and means only "not from below".
    const a = legacyFloor ? null : anchorRadius();
    const base = a ?? radiusM();
    const clear = a === null ? base : base + EYE_CLEAR_FRACTION * state.altitudeM;
    // The aim's convergence does not enter here: the eye holds the converged
    // pose's own radius at every convergence, so one floor covers them all.
    const floor = Math.max(-PITCH_LIMIT, pitchFloor(state.altitudeM, base, clear));
    return Math.min(ceilingAt(state.altitudeM), Math.max(floor, p));
  }

  /**
   * Raise the pitch until the eye clears the ground UNDER it.
   *
   * The floor above is exact for a sphere through the anchor, and the terrain is
   * not that sphere: a far-side approach at Jezero's own radius crosses ground
   * kilometres higher. Solved rather than stepped - the same law of cosines,
   * re-read three times because the ground moves as the eye does - and only
   * ever upward, so a legitimate low approach keeps the pitch the player set.
   */
  function clearEye(): void {
    clearEyePitch();
    floorEyeToGround();
  }

  function clearEyePitch(): void {
    if (legacyFloor || !bodies.groundRadiusM) return;
    const a = anchorRadius();
    if (a === null) return;
    const centre = bodies.centre(state.bodyId) ?? { x: 0, y: 0, z: 0 };
    for (let i = 0; i < 3; i++) {
      place();
      const g = bodies.groundRadiusM(state.bodyId, eye);
      if (g === null) return;
      const clear = g + EYE_CLEAR_FRACTION * state.altitudeM;
      const r = Math.hypot(eye.x - centre.x, eye.y - centre.y, eye.z - centre.z);
      if (r >= clear) return;
      const want = pitchFloor(state.altitudeM, a, clear);
      const next = Math.min(ceilingAt(state.altitudeM), Math.max(state.pitch, want));
      if (next <= state.pitch) return;
      state.pitch = next;
    }
  }

  /**
   * Raise the ALTITUDE until the eye clears the ground under it.
   *
   * The pitch floor above cannot answer an eye already inside the terrain: no
   * pitch lifts it out, and the focus point the altitude is measured from is
   * itself easing onto the ground at a capped rate, so a wheel spun faster than
   * that cap reaches the anchor while the anchor is still under Tharsis. Held
   * on the altitude and not by moving the eye, so the number the card shows
   * stays the eye's own height; raising it walks the eye out along the same
   * arc, so a metre of altitude is only the pitch's sine of a metre of height,
   * and the step is divided by what the last one measured. A step of the
   * deficit alone left a third of it at the walkaround's pitch, and every
   * later notch in closed some of that and raised the eye.
   */
  function floorEyeToGround(): void {
    groundFloorHeldM = 0;
    if (legacyFloor || config.groundFloorOff?.() === true) return;
    if (!bodies.eyeFloorRadiusM) return;
    // A standing eye is placed by `standEye`, not by the altitude, so this floor could never be met and read
    // as binding; focusGround's `holdEyeRadius` then stepped the altitude out of SURFACE before the plot mounted.
    if (!legacyStandSolve && bodies.groundRadiusM
      && bodies.standingEyeFloorM?.(state.bodyId, state.altitudeM, steppedIn) != null) return;
    const centre = bodies.centre(state.bodyId) ?? { x: 0, y: 0, z: 0 };
    const from = state.altitudeM;
    let lastAlt = NaN;
    let lastR = NaN;
    for (let i = 0; i < 6; i++) {
      place();
      const f = bodies.eyeFloorRadiusM(state.bodyId, eye);
      if (f === null || !Number.isFinite(f) || f <= 0) break;
      const r = Math.hypot(eye.x - centre.x, eye.y - centre.y, eye.z - centre.z);
      // A standing eye is `standEye`'s to hold, to its own 5 cm: the mast floor
      // over it raised the altitude on every call until the eye left the band.
      const stand = legacyStandSolve ? null : bodies.standingEyeFloorM?.(state.bodyId, state.altitudeM, steppedIn) ?? null;
      const lift = f + (stand ?? EYE_GROUND_FLOOR_M) - r;
      const tol = stand === null ? 0.01 : 0.05;
      // An overshoot steps back down, never under the altitude the caller asked for.
      if (lift <= tol && (state.altitudeM === from || lift >= -tol)) break;
      const slope = Number.isFinite(lastAlt) && state.altitudeM !== lastAlt
        ? (r - lastR) / (state.altitudeM - lastAlt) : Math.sin(Math.max(0, state.pitch));
      lastAlt = state.altitudeM;
      lastR = r;
      const next = Math.max(from, Math.min(1e15, state.altitudeM + lift / THREE.MathUtils.clamp(slope, 0.05, 1)));
      if (next === state.altitudeM) break;
      state.altitudeM = next;
    }
    groundFloorHeldM = state.altitudeM - from;
  }

  // A centre anchor's altitude is measured on the DATUM, where a surface
  // anchor's is measured on the terrain, so the eye is lifted by the drawn
  // ground's own height over it - radially, which leaves its lat/lon alone.
  function centreGroundLiftM(): number {
    if (legacyFloor || !bodies.groundRadiusM || !centreAnchored()) return 0;
    const g = bodies.groundRadiusM(state.bodyId, eye);
    if (g === null) return 0;
    return Math.max(0, g - radiusM());
  }

  /**
   * Turn the basis from the body's own pole onto the focus point's radial.
   *
   * `_up` is the axis a drag turns the eye about and the screen's own up, so
   * a basis on the site holds that point still on screen and swings the body
   * around it - the planet turning about a place on its ground rather than
   * about itself, which is what a drag at ORBIT read as. The aim is already
   * the body's centre up there; the basis follows it, on the same
   * convergence, so the site's frame is the basis exactly where the site is
   * the subject and neither band edge steps.
   *
   * Slerped, not lerped: the two are up to 90 degrees apart and a straight
   * blend between them turns fastest in the middle of the band.
   */
  function turnAxis(poleVec: WorldVec | null, mix: number): void {
    if (legacySiteAxis || !poleVec || mix >= 1) return;
    vec(poleVec, _pole).normalize();
    const w = Math.acos(THREE.MathUtils.clamp(_pole.dot(_up), -1, 1));
    const sw = Math.sin(w);
    // Coincident, or a focus point on the far end of the axis itself: there is
    // no great circle between them to turn along, and the site's own frame is
    // as good an answer as any.
    if (sw < 1e-6) {
      if (w < 1e-6) _up.copy(_pole);
      return;
    }
    _up.multiplyScalar(Math.sin(mix * w) / sw)
      .addScaledVector(_pole, Math.sin((1 - mix) * w) / sw)
      .normalize();
  }

  /**
   * The blended pose `solve` left for `place` to finish; mix -1 off a blend.
   *
   * The distance solve needs the eye direction, the direction needs the basis,
   * and the basis is what `solve` is building, so a blended pose cannot be
   * closed inside it. It leaves the focus point's radius and the convergence
   * here and `place` reads the distance once `_dir` stands.
   */
  let blendMix = -1;
  let blendAnchorRadiusM = 0;

  /** Fill `anchor` and the local basis; returns the eye's distance from it. */
  function solve(): number {
    const r = radiusM();
    const centre = bodies.centre(state.bodyId) ?? { x: 0, y: 0, z: 0 };
    const poleVec = bodies.pole(state.bodyId);
    setYawAxis(poleVec);
    blendMix = -1;
    if (centreAnchored() || state.latDeg === null || state.lonDeg === null) {
      anchor.x = centre.x; anchor.y = centre.y; anchor.z = centre.z;
      if (poleVec) vec(poleVec, _up).normalize();
      else _up.copy(YSCENE);
      return r + state.altitudeM;
    }
    const s = bodies.surface(state.bodyId, state.latDeg, state.lonDeg);
    if (!s) {
      anchor.x = centre.x; anchor.y = centre.y; anchor.z = centre.z;
      _up.copy(YSCENE);
      return r + state.altitudeM;
    }
    // What the convergence moves is the aim, the basis and the eye's distance
    // from the aim, all three continuous in it: down the whole descent the
    // frame eases from the body's own axis onto the focus point's local one
    // and nothing turns over at a band edge.
    _siteHat.set(s.x - centre.x, s.y - centre.y, s.z - centre.z);
    const a = _siteHat.length();
    _up.copy(_siteHat).normalize();
    const mix = aimMix();
    turnAxis(poleVec, mix);
    if (mix >= 1) {
      anchor.x = s.x; anchor.y = s.y; anchor.z = s.z;
      const stand = legacyFloor
        ? null : bodies.standingEyeFloorM?.(state.bodyId, state.altitudeM, steppedIn) ?? null;
      if (stand === null) return state.altitudeM;
      // Carried on the DISTANCE, not by lifting the eye: `place()` puts the eye
      // `dist` from the anchor at `pitch` and `apply()` aims at the anchor, so
      // raising the eye alone would tilt the whole view down into the ground.
      // `atlas.ts`'s `plotDistanceFromAltitude` floors the walkaround's own rig
      // the same way and records the same reason.
      return Math.max(state.altitudeM, stand / Math.max(0.02, Math.sin(Math.max(0, state.pitch))));
    }
    _siteHat.multiplyScalar(a === 0 ? 0 : 1 / a);
    anchor.x = centre.x + (s.x - centre.x) * mix;
    anchor.y = centre.y + (s.y - centre.y) * mix;
    anchor.z = centre.z + (s.z - centre.z) * mix;
    blendMix = mix;
    blendAnchorRadiusM = a;
    // `place` reads it off `_dir`; this branch's distance is not knowable yet.
    return 0;
  }

  /** Put the eye `d` from the anchor along `_dir`. */
  function eyeAt(d: number): void {
    eye.x = anchor.x + _dir.x * d;
    eye.y = anchor.y + _dir.y * d;
    eye.z = anchor.z + _dir.z * d;
  }

  /**
   * Hold the standing eye over the ground UNDER IT, not over the anchor's.
   *
   * `solve()` floors the DISTANCE, which sets the eye's height over the ANCHOR;
   * on a body 1.9 km out of round the ground twelve metres away is a metre
   * higher, and the person is knee deep in it. Solved on the eye's own ground
   * the way `clearEye` is, and both ways, since the criterion is a height and
   * not a clearance. Never nearer the anchor than the standing distance the
   * ladder solved: that distance is what the aim's elevation is made of, and the
   * solve on the anchor's own sphere is ill-conditioned downhill - a metre of
   * drop under the eye asks for 5 m where the ladder asked for 12, and 1.7 m
   * asks for zero, which stands the person on the anchor looking 45 degrees
   * into it. Ground that falls away is the residual's, below.
   */
  function standEye(dist: number): void {
    const stand = legacyFloor
      ? null : bodies.standingEyeFloorM?.(state.bodyId, state.altitudeM, steppedIn) ?? null;
    if (stand === null || !bodies.groundRadiusM) return;
    const a = anchorRadius();
    if (a === null) return;
    const centre = bodies.centre(state.bodyId) ?? { x: 0, y: 0, z: 0 };
    const sp = Math.sin(Math.max(0, state.pitch));
    let d = dist;
    let prevD = d;
    let prevErr = Infinity;
    for (let i = 0; i < 3; i++) {
      const g = bodies.groundRadiusM(state.bodyId, eye);
      if (g === null) return;
      const r = Math.hypot(eye.x - centre.x, eye.y - centre.y, eye.z - centre.z);
      const target = g + stand;
      const err = Math.abs(r - target);
      if (err < 0.05) return;
      // Ground climbing faster than the pitch walks the eye up the hill without
      // end (1.4 km out on Titan's polar relief): keep the nearer distance.
      if (err >= prevErr && !legacyStandSolve) {
        eyeAt(prevD);
        break;
      }
      prevErr = err;
      prevD = d;
      // The law of cosines `clearEye` solves, for the distance that puts the eye
      // on the target radius at this pitch: |anchor + d * dir| = target.
      const root = a * a * sp * sp + target * target - a * a;
      if (root < 0) break;
      const next = Math.max(dist, Math.sqrt(root) - a * sp);
      if (!Number.isFinite(next) || Math.abs(next - d) < 1e-4) break;
      d = next;
      eyeAt(d);
    }
    // What the distance cannot reach, because the ground rises or falls away
    // faster than this pitch does, taken radially at the eye instead: a radial
    // step keeps the eye's own lat/lon, so the ground under it does not move and
    // one step is exact. It costs a degree or two of aim, which is why it is the
    // residual and not the whole floor.
    const g = bodies.groundRadiusM(state.bodyId, eye);
    if (g === null) return;
    const r = Math.hypot(eye.x - centre.x, eye.y - centre.y, eye.z - centre.z);
    const lift = g + stand - r;
    if (r <= 0 || Math.abs(lift) < 0.02) return;
    eye.x += ((eye.x - centre.x) / r) * lift;
    eye.y += ((eye.y - centre.y) / r) * lift;
    eye.z += ((eye.z - centre.z) / r) * lift;
  }

  /**
   * What the standing pose looks AT: the landing point's bearing carried onto
   * the ground under the EYE, not the landing point itself.
   *
   * `standEye` holds the eye over its own ground, so where the body is far out
   * of round the landing point sits metres downhill of where the person stands
   * and a look straight at it drops the horizon off the top of the frame -
   * 23.6 degrees down on Vesta against a horizon 0.21 down and a half-fov of
   * 22.5. Raising the aim onto the eye's own shell is what level ground already
   * gives, so nothing moves where the two shells agree, and the lift grows with
   * the drop rather than switching on at a band edge.
   */
  function standingAim(out: THREE.Vector3): boolean {
    const stand = legacyFloor
      ? null : bodies.standingEyeFloorM?.(state.bodyId, state.altitudeM, steppedIn) ?? null;
    if (stand === null || !bodies.groundRadiusM) return false;
    const g = bodies.groundRadiusM(state.bodyId, eye);
    if (g === null || !(g > 0)) return false;
    const centre = bodies.centre(state.bodyId) ?? { x: 0, y: 0, z: 0 };
    out.set(anchor.x - centre.x, anchor.y - centre.y, anchor.z - centre.z);
    const a = out.length();
    // A centre anchor has no bearing to carry, and no landing point either.
    if (a === 0) return false;
    out.multiplyScalar(g / a);
    out.x += centre.x; out.y += centre.y; out.z += centre.z;
    return true;
  }

  /**
   * The direction yaw 0 is measured from, for an up anywhere on the arc the
   * convergence walks.
   *
   * Built at the POLE and carried onto `up` by the same turn that took the pole
   * there, rather than crossed with the scene reference at `up` itself. The
   * reference then only has to stand clear of the POLE, which is the one vector
   * `setYawAxis` tests it against, and the two can no longer close up partway
   * down: the frame is the pole's own, rigidly turned, so it is continuous in
   * the convergence at every site. Crossed at `up` instead, a site whose blend
   * passes near the reference throws the eye 180 degrees round it on a quarter
   * of a wheel notch - and with the wheel untouched, the body's own spin
   * carries every site at that colatitude across it twice a day.
   * `descent-dojo --crossing --inject pole-yaw-axis` is that reading;
   * `--inject basis-yaw-axis` is the older one, picked off the turning basis.
   */
  function poseEast(up: THREE.Vector3, out: THREE.Vector3): THREE.Vector3 {
    if (legacyYawAxis) {
      out.copy(Math.abs(up.dot(YSCENE)) > 0.999 ? XSCENE : YSCENE).cross(up).normalize();
    } else if (legacyPoleYawAxis) {
      out.copy(_yawAxis).cross(up).normalize();
    } else {
      out.copy(_yawAxis).cross(_yawPole).normalize();
      if (out.lengthSq() >= 1e-12) {
        out.applyQuaternion(_yawTurn.setFromUnitVectors(_yawPole, up)).normalize();
      }
    }
    if (out.lengthSq() < 1e-12) out.set(1, 0, 0);
    return out;
  }

  /**
   * Where the eye sits around an anchor whose local up is `up`, as a unit
   * vector, filling the basis as it goes.
   *
   * The reference direction only has to be stable, so yaw 0 is repeatable;
   * which way it points is cosmetic. Stable is the whole of it, though, which
   * is why it comes from `poseEast` and not from `up` itself.
   */
  function poseDir(up: THREE.Vector3, out: THREE.Vector3): THREE.Vector3 {
    poseEast(up, _east);
    _north.copy(up).cross(_east).normalize();
    const cp = Math.cos(state.pitch);
    const sp = Math.sin(state.pitch);
    return out.set(0, 0, 0)
      .addScaledVector(_north, cp * Math.cos(state.yaw))
      .addScaledVector(_east, cp * Math.sin(state.yaw))
      .addScaledVector(up, sp)
      .normalize();
  }

  function place(): void {
    let dist = solve();
    poseDir(_up, _dir);
    // The blend `solve` left open: its distance needs `_dir`, which needs the
    // basis `poseDir` has just built.
    if (blendMix >= 0) dist = aimDistanceM(blendAnchorRadiusM, blendMix, _siteHat.dot(_dir));
    eyeAt(dist);
    standEye(dist);
    const lift = centreGroundLiftM();
    if (lift > 0) {
      eye.x += _dir.x * lift;
      eye.y += _dir.y * lift;
      eye.z += _dir.z * lift;
    }
  }

  const handle: FocusCameraHandle = {
    state,
    band(): Band {
      return bandAt(state.altitudeM, radiusM());
    },
    tier(): number {
      return tierAt(state.altitudeM, radiusM());
    },
    radiusM,
    get groundFloorHeldM(): number { return groundFloorHeldM; },
    get wheelZoomed(): boolean { return wheelZoomed; },
    get standEaseOwed(): boolean { return standEase !== 'none'; },
    noteStandEase(ease: 'moving' | 'closed' | 'stood'): void {
      if (standEase === 'none') return;
      if (ease === 'moving') standEase = 'easing';
      else if (ease === 'stood' || standEase === 'easing') standEase = 'none';
    },
    get zoomInSteps(): number { return zoomInSteps; },
    get steppedIntoSurface(): boolean { return steppedIn; },
    zoom(notches: number): void {
      wheelZoomed = true;
      if (notches < 0) zoomInSteps++;
      const from = state.altitudeM;
      state.altitudeM = clampAltitude(state.altitudeM * Math.pow(ZOOM_PER_NOTCH, notches));
      noteStep(from);
      if (bandAt(state.altitudeM, radiusM()) !== 'SURFACE') standEase = 'none';
      state.pitch = clampPitch(wantPitch ?? state.pitch);
      // The followed eye runs under ground still streaming in until the plot mounts and stands it;
      // floored or pitch-cleared at a notch there, it rose 245 m at Jezero and the walkaround stood 82 m up.
      if (standEase === 'none') clearEye();
      else groundFloorHeldM = 0;
    },
    setAltitude(metres: number): void {
      if (!Number.isFinite(metres)) return;
      wheelZoomed = false;
      const from = state.altitudeM;
      state.altitudeM = clampAltitude(metres);
      noteStep(from);
      askStand();
      state.pitch = clampPitch(wantPitch ?? state.pitch);
      clearEye();
    },
    reclampAltitude(): void {
      const from = state.altitudeM;
      state.altitudeM = clampAltitude(state.altitudeM);
      noteStep(from);
      state.pitch = clampPitch(wantPitch ?? state.pitch);
      clearEye();
    },
    holdEyeRadius(radiusFromCentreM: number): void {
      if (!Number.isFinite(radiusFromCentreM) || radiusFromCentreM <= 0) return;
      const centre = bodies.centre(state.bodyId) ?? { x: 0, y: 0, z: 0 };
      for (let i = 0; i < 4; i++) {
        place();
        const r = Math.hypot(eye.x - centre.x, eye.y - centre.y, eye.z - centre.z);
        const off = radiusFromCentreM - r;
        if (Math.abs(off) < 0.05) break;
        // The altitude is measured along the aim and the radius is measured
        // from the centre, so a metre of one is a metre of the other only
        // overhead; stepped rather than solved because the blend is in it too.
        state.altitudeM = clampAltitude(
          state.altitudeM + off / Math.max(0.05, Math.sin(Math.max(0, state.pitch))));
      }
      state.pitch = clampPitch(state.pitch);
      clearEye();
    },
    orbitBy(dYaw: number, dPitch: number): void {
      if (dPitch !== 0) wantPitch = null;
      state.yaw += dYaw;
      state.pitch = clampPitch(state.pitch + dPitch);
      clearEye();
    },
    setOrientation(yaw: number, pitch: number, remember?: number): void {
      // A yaw-only caller hands the current pitch back and must not re-arm the follow.
      // `remember` is the pitch zooming back out restores, where it is not the one set now.
      if (pitch !== state.pitch || remember !== undefined) wantPitch = remember ?? pitch;
      state.yaw = yaw;
      state.pitch = clampPitch(pitch);
      clearEye();
    },
    pinPitch(): void {
      wantPitch = null;
    },
    pitchMemory(): number | null {
      return wantPitch;
    },
    faceDirection(dirWorld: WorldVec): void {
      _face.set(dirWorld.x, dirWorld.y, dirWorld.z);
      if (_face.lengthSq() === 0) return;
      _face.normalize();
      const poleVec = bodies.pole(state.bodyId);
      setYawAxis(poleVec);
      if (poleVec) vec(poleVec, _up).normalize();
      else _up.copy(YSCENE);
      // The same frame `poseDir` builds, off the same call, so the yaw and
      // pitch solved here are the pair the pose puts back.
      poseEast(_up, _east);
      _north.copy(_up).cross(_east).normalize();
      state.yaw = Math.atan2(_face.dot(_east), _face.dot(_north));
      state.pitch = clampPitch(Math.asin(THREE.MathUtils.clamp(_face.dot(_up), -1, 1)));
      clearEye();
    },
    setAnchor(latDeg: number | null, lonDeg: number | null): void {
      const centreOnly = bodies.centreAnchorOnly?.(state.bodyId) ?? false;
      state.latDeg = centreOnly ? null : latDeg;
      state.lonDeg = centreOnly ? null : lonDeg;
    },
    yawFrame(north: THREE.Vector3, east: THREE.Vector3): void {
      place();
      poseEast(_up, east);
      north.copy(_up).cross(east).normalize();
    },
    poseAnchorDir(): WorldVec | null {
      const centre = bodies.centre(state.bodyId) ?? { x: 0, y: 0, z: 0 };
      place();
      _probeEye.set(eye.x - centre.x, eye.y - centre.y, eye.z - centre.z);
      if (_probeEye.lengthSq() === 0) return null;
      _probeEye.normalize();
      _probeUp.copy(_probeEye);
      for (let i = 0; i < 4; i++) {
        poseDir(_probeUp, _probeDir);
        _probeQ.setFromUnitVectors(_probeDir, _probeEye);
        _probeUp.applyQuaternion(_probeQ).normalize();
      }
      return { x: _probeUp.x, y: _probeUp.y, z: _probeUp.z };
    },
    goTo(bodyId: string, latDeg: number | null, lonDeg: number | null, altitudeM: number): void {
      state.bodyId = bodyId;
      // A body with no surface has no surface point to focus on, so the centre
      // is the anchor whatever lat/lon the caller asked for - and the state says
      // so, rather than carrying a lat/lon that nothing reads.
      const centreOnly = bodies.centreAnchorOnly?.(bodyId) ?? false;
      state.latDeg = centreOnly ? null : latDeg;
      state.lonDeg = centreOnly ? null : lonDeg;
      wheelZoomed = false;
      state.altitudeM = clampAltitude(altitudeM);
      askStand();
      steppedIn = false;
      state.pitch = clampPitch(state.pitch);
      if (wantPitch !== null) wantPitch = state.pitch;
      clearEye();
    },
    fitAll(aspect: number, scope: FramingScope = 'opening', keepPitchMemory = false): void {
      let targets = bodies.framingTargets(scope);
      // ORIGIN on its own frames nothing: before the registry lands there is no
      // body in the fit at all, so it falls back to the pick criterion's set.
      if (targets.length < 2) targets = bodies.framingTargets('all');
      let rMax = 0;
      for (const t of targets) rMax = Math.max(rMax, Math.hypot(t.x, t.y, t.z));
      const tanV = Math.tan((camera.fov * 0.5 * Math.PI) / 180);
      const tan = tanV * Math.min(1, Math.max(0.2, aspect));
      // 0.86 of the half-frame: the margin covers a body on the near side of
      // the ecliptic projecting further out than its orbital radius implies.
      const dist = rMax > 0 ? rMax / (0.86 * tan) : 1.9e13;
      state.bodyId = 'sol';
      state.latDeg = null;
      state.lonDeg = null;
      state.yaw = 0.6;
      state.altitudeM = clampAltitude(dist - (bodies.radiusM('sol') ?? 0));
      state.pitch = clampPitch(1.33);
      if (!keepPitchMemory) wantPitch = null;
    },
    eyeWorld(): WorldVec {
      place();
      return eye;
    },
    setLegacyPitchFloor(on: boolean): void {
      legacyFloor = on;
    },
    setLegacySitePivot(on: boolean): void {
      legacySitePivot = on;
    },
    setLegacySiteAxis(on: boolean): void {
      legacySiteAxis = on;
    },
    setLegacyEyeOblique(on: boolean): void {
      legacyEyeOblique = on;
      state.pitch = clampPitch(state.pitch);
      clearEye();
    },
    setLegacyOrbitPivot(on: boolean): void {
      legacyOrbitPivot = on;
      state.pitch = clampPitch(state.pitch);
      clearEye();
    },
    setLegacyYawAxis(on: boolean): void {
      legacyYawAxis = on;
      clearEye();
    },
    setLegacyStandSolve(on: boolean): void {
      legacyStandSolve = on;
    },
    setLegacyPoleYawAxis(on: boolean): void {
      legacyPoleYawAxis = on;
      clearEye();
    },
    aim(): FocusAim {
      const a = anchorRadius();
      const mix = a === null ? null : aimMix();
      // Both arcs are measured off the PLACED eye. They used to follow from
      // the pitch and the two distances alone, which held while the basis was
      // the focus point's own frame at every altitude; it turns with the aim
      // now, so the pitch no longer says where the eye sits about either one.
      place();
      const centre = bodies.centre(state.bodyId) ?? { x: 0, y: 0, z: 0 };
      _radial.set(eye.x - centre.x, eye.y - centre.y, eye.z - centre.z);
      const arcTo = (v: WorldVec | null, fromCentre: boolean): number | null => {
        if (!v || _radial.lengthSq() === 0) return null;
        _h.set(v.x, v.y, v.z);
        if (fromCentre) _h.set(v.x - centre.x, v.y - centre.y, v.z - centre.z);
        if (_h.lengthSq() === 0) return null;
        return +((_radial.angleTo(_h) * 180) / Math.PI).toFixed(2);
      };
      const s = a === null || state.latDeg === null || state.lonDeg === null
        ? null : bodies.surface(state.bodyId, state.latDeg, state.lonDeg);
      return {
        convergence: mix,
        anchorRadiusM: a,
        eyeArcDeg: arcTo(s, true),
        poleArcDeg: arcTo(bodies.pole(state.bodyId), false),
        sitePivot: legacySitePivot,
        siteAxis: legacySiteAxis,
        orbitPivot: legacyOrbitPivot,
        yawAxisFromBasis: legacyYawAxis,
        yawAxisFromPole: legacyPoleYawAxis,
        yawAxis: _yawAxis.equals(XSCENE) ? 'x' : 'y',
      };
    },
    apply(): void {
      place();
      camera.position.set(0, 0, 0);
      camera.up.copy(_up);
      _look.set(anchor.x - eye.x, anchor.y - eye.y, anchor.z - eye.z);
      if (standingAim(_aim)) _look.set(_aim.x - eye.x, _aim.y - eye.y, _aim.z - eye.z);
      const lift = bodies.lookLiftRad?.(state.bodyId, state.altitudeM) ?? null;
      // Only on a centre anchor, where the look is exactly antiparallel to the
      // eye's own radial and has no horizontal part of its own to raise: the
      // horizontal is taken from the pole instead, so the view runs along the
      // limb toward the north pole and yaw still turns the body under it.
      if (lift !== null && centreAnchored()) {
        _radial.copy(_dir);
        _h.copy(_up).addScaledVector(_radial, -_up.dot(_radial));
        if (_h.lengthSq() < 1e-12) _h.copy(_east);
        _h.normalize();
        _look.copy(_h).multiplyScalar(Math.cos(lift)).addScaledVector(_radial, Math.sin(lift));
      }
      camera.lookAt(_look);
      camera.updateMatrixWorld();
    },
    dispose(): void {
      // Nothing owned; the handle keeps the repo's createX shape.
    },
  };
  return handle;
}
